import React from "react";

export type MerchantPlatform = "shopify" | "woocommerce" | "custom" | "square" | "lightspeed";

export interface MerchantAppBranding {
  platform: MerchantPlatform;
  label: string;
  pluginLabel: string;
  Logo: React.FC;
}

const ShopifyLogo = () => (
  <svg className="zulu-plugin__shop-logo" viewBox="0 0 256 292" xmlns="http://www.w3.org/2000/svg" aria-label="Shopify">
    <path fill="#95BF47" d="M223.4 57.6c-.2-1.4-1.4-2.3-2.7-2.4-1.3-.1-28.6-2.1-28.6-2.1s-18.9-18.6-21-20.7c-2-2.1-5.9-1.4-7.4-.8 0 0-3.6 1.1-9.6 3C146.4 16.8 134.8 0 121.3 0c-1.3 0-2.6.1-3.8.3C112.4 1.4 99.8 5.4 91 26.2 82.3 22.8 64.2 16.4 64.2 16.4s-4.8-1.5-6.8.9c-2.1 2.4-7 9.7-7.8 11.2-.8 1.5-1.6 4.4.3 6.6 0 0 1.1 1.2 3.3 2.4-2.6 8-6.5 24.3-4.3 42.4-15.2 4.6-25.4 7.8-26.5 8.2-10.1 3.1-10.4 3.4-11.7 13C10 106.4.1 247.7.1 247.7l166.8 31.5 90.3-19.7S223.6 59 223.4 57.6z" />
    <path fill="#5E8E3E" d="M223.4 57.6c-.2-1.4-1.4-2.3-2.7-2.4-1.3-.1-28.6-2.1-28.6-2.1s-18.9-18.6-21-20.7c-.6-.6-1.3-1-2-.8v246.6l90.3-19.7S223.6 59 223.4 57.6z" />
    <path fill="#fff" d="M132.2 81.4s-5.2-2.8-11.5-2.8c-9.4 0-9.8 5.9-9.8 7.4 0 8.1 21.2 11.2 21.2 26.9 0 13.3-8.5 21.8-19.9 21.8-13.7 0-20.6-8.5-20.6-8.5l3.6-12s7.1 6.1 13.1 6.1c4 0 5.6-3.1 5.6-5.4 0-9.4-17.3-9.8-17.3-25.3 0-13 8.6-25.6 26.2-25.6 6.4 0 9.6 1.9 9.6 1.9l-2.2 15.5z" />
  </svg>
);

const WooCommerceLogo = () => (
  <svg className="zulu-plugin__shop-logo" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="WooCommerce">
    <rect width="24" height="24" rx="6" fill="#7F54B3" />
    <path fill="#fff" d="M6.8 8.2h2.1l1.3 5.4 1.4-5.4h1.8l1.4 5.4 1.3-5.4h2.1l-2.5 7.6H13.4L12 10.2l-1.4 5.6H8.3L6.8 8.2z" />
  </svg>
);

const CustomLogo = () => (
  <svg className="zulu-plugin__shop-logo" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="Custom store">
    <rect width="24" height="24" rx="6" fill="#F75D21" />
    <path fill="#fff" d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h6v2H7v-2z" />
  </svg>
);

const SquareLogo = () => (
  <svg className="zulu-plugin__shop-logo" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="Square">
    <rect width="24" height="24" rx="5" fill="#000" />
    <rect x="6" y="6" width="12" height="12" rx="2" fill="#fff" />
  </svg>
);

const LightspeedLogo = () => (
  <svg className="zulu-plugin__shop-logo" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="Lightspeed">
    <rect width="24" height="24" rx="6" fill="#E31C3D" />
    <path fill="#fff" d="M8 6.5h3.2L8.8 12H12v5.5H8.8V12H6.2L8 6.5zM13.4 6.5H18L15.6 12H18v5.5h-4.6V6.5z" />
  </svg>
);

const BRANDING: Record<MerchantPlatform, MerchantAppBranding> = {
  shopify: {
    platform: "shopify",
    label: "Shopify",
    pluginLabel: "Shopify Plugin",
    Logo: ShopifyLogo,
  },
  woocommerce: {
    platform: "woocommerce",
    label: "WooCommerce",
    pluginLabel: "WooCommerce Plugin",
    Logo: WooCommerceLogo,
  },
  custom: {
    platform: "custom",
    label: "Custom store",
    pluginLabel: "Zulu Merchant Portal",
    Logo: CustomLogo,
  },
  square: {
    platform: "square",
    label: "Square",
    pluginLabel: "Square Plugin",
    Logo: SquareLogo,
  },
  lightspeed: {
    platform: "lightspeed",
    label: "Lightspeed",
    pluginLabel: "Lightspeed Plugin",
    Logo: LightspeedLogo,
  },
};

export function isLiveMerchantPlatform(platform?: string | null): boolean {
  return platform === "shopify" || platform === "woocommerce" || platform === "custom";
}

export function parseMerchantPlatform(value?: string | null): MerchantPlatform {
  const normalized = String(value || "").trim().toLowerCase();
  if (
    normalized === "woocommerce" ||
    normalized === "custom" ||
    normalized === "square" ||
    normalized === "lightspeed"
  ) {
    return normalized;
  }
  return "shopify";
}

export function getMerchantAppBranding(platform?: string | null): MerchantAppBranding {
  return BRANDING[parseMerchantPlatform(platform)];
}
